import { useState } from 'react'
import {
  X,
  Save,
  Loader2,
  Eye,
  EyeOff,
  AlertCircle,
  Phone,
  MessageSquare,
  ShieldCheck,
  Send,
  HelpCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  type ChannelInstanceResponse,
  updateChannelInstance,
  validateTelegramToken,
} from '@/services/channelsApi'
import { extractApiErrorMessage } from '@/utils/apiError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface EditChannelInstanceModalProps {
  instance: ChannelInstanceResponse
  onClose: () => void
  onSuccess: (updated: ChannelInstanceResponse) => void
}

export default function EditChannelInstanceModal({
  instance,
  onClose,
  onSuccess,
}: EditChannelInstanceModalProps) {
  const isCloud = instance.channelType === 'WHATSAPP_CLOUD'
  const isTelegram = instance.channelType === 'TELEGRAM'

  // Form states
  const [instanceName, setInstanceName] = useState(instance.instanceName || '')
  const [phoneNumberId, setPhoneNumberId] = useState(
    String(instance.config?.phoneNumberId || '')
  )
  const [accessToken, setAccessToken] = useState('')
  const [wabaId, setWabaId] = useState(String(instance.config?.wabaId || ''))
  const [showToken, setShowToken] = useState(false)

  // Telegram states
  const [botToken, setBotToken] = useState('')
  const [telegramReceiveMode, setTelegramReceiveMode] = useState<'webhook' | 'polling'>(
    instance.config?.receiveMode === 'polling' ? 'polling' : 'webhook'
  )
  const [validatingTelegram, setValidatingTelegram] = useState(false)

  // Submission & error states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleValidateTelegram = async () => {
    const token = botToken.trim()
    if (!token) {
      toast.error('Ingresa un bot token para validar')
      return
    }

    try {
      setValidatingTelegram(true)
      const res = await validateTelegramToken(token)
      if (res.valid && res.bot) {
        toast.success(`Token válido para @${res.bot.username || res.bot.firstName}`)
      } else {
        toast.error(res.error || 'Token de Telegram inválido')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Error al validar con Telegram')
    } finally {
      setValidatingTelegram(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedName = instanceName.trim()
    if (!trimmedName) {
      setErrorMessage('El nombre de la instancia es obligatorio.')
      return
    }

    const configUpdates: Record<string, any> = {}

    if (isCloud) {
      const cleanPhone = phoneNumberId.trim()
      if (!cleanPhone) {
        setErrorMessage('El Phone Number ID es obligatorio para WhatsApp Cloud API.')
        return
      }
      configUpdates.phoneNumberId = cleanPhone

      // Si el usuario ingresó un nuevo token, enviarlo; de lo contrario se conserva en backend
      if (accessToken.trim()) {
        configUpdates.accessToken = accessToken.trim()
      }

      if (wabaId.trim()) {
        configUpdates.wabaId = wabaId.trim()
      }
    } else if (isTelegram) {
      if (botToken.trim()) {
        configUpdates.botToken = botToken.trim()
      }
      configUpdates.receiveMode = telegramReceiveMode
    }

    try {
      setIsSubmitting(true)
      setErrorMessage(null)

      const updated = await updateChannelInstance(instance.id, {
        instanceName: trimmedName,
        config: configUpdates,
      })

      toast.success('Instancia actualizada exitosamente')
      onSuccess(updated)
    } catch (err: any) {
      const { message } = extractApiErrorMessage(err, 'Error al actualizar la instancia')
      setErrorMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-instance-title"
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Cerrar modal"
          className="absolute right-4 top-4 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <div
            className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${
              isCloud
                ? 'bg-teal-50 border-teal-200 text-teal-800'
                : isTelegram
                ? 'bg-sky-50 border-sky-200 text-sky-600'
                : 'bg-emerald-50 border-emerald-200 text-[#075E54]'
            }`}
          >
            {isTelegram ? (
              <Send className="w-6 h-6" />
            ) : (
              <MessageSquare className="w-6 h-6" />
            )}
          </div>
          <div>
            <h2 id="edit-instance-title" className="text-xl font-bold text-slate-900 leading-tight">
              {isCloud ? 'Editar WhatsApp Cloud API' : 'Editar Instancia de Canal'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              ID: <span className="font-mono text-slate-700">{instance.id}</span>
            </p>
          </div>
        </div>

        {/* Cloud API Current Status Banner */}
        {isCloud && (
          <div className="mt-4 rounded-2xl bg-teal-50/60 border border-teal-100 p-3.5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-teal-900 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-teal-700" />
                Conexión Oficial Meta Graph
              </span>
              <span
                className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                  instance.status === 'CONNECTED'
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-amber-100 text-amber-800'
                }`}
              >
                {instance.status === 'CONNECTED' ? 'Conectado' : instance.status}
              </span>
            </div>

            {instance.metadata?.displayPhoneNumber && (
              <div className="flex items-center gap-2 text-xs text-teal-800 font-medium">
                <Phone className="w-3.5 h-3.5 text-teal-600" />
                <span>Número verificado: {instance.metadata.displayPhoneNumber}</span>
                {instance.metadata?.verifiedName && (
                  <span className="text-slate-500 font-normal">
                    ({instance.metadata.verifiedName})
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error Alert */}
        {errorMessage && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-2xl text-xs text-red-700 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">Error al guardar</p>
              <p className="mt-0.5 whitespace-pre-line">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          {/* Instance Name */}
          <div>
            <label
              htmlFor="edit-instance-name"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1"
            >
              Nombre de la instancia <span className="text-red-500">*</span>
            </label>
            <Input
              id="edit-instance-name"
              type="text"
              value={instanceName}
              onChange={(e) => setInstanceName(e.target.value)}
              placeholder="Ej: WhatsApp Ventas Oficial"
              disabled={isSubmitting}
              maxLength={100}
              className="text-sm rounded-xl border-slate-200"
              required
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Nombre interno para identificar esta línea dentro de tu panel.
            </p>
          </div>

          {/* Cloud API Specific Fields */}
          {isCloud && (
            <>
              {/* Phone Number ID */}
              <div>
                <label
                  htmlFor="edit-phone-number-id"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1"
                >
                  Phone Number ID <span className="text-red-500">*</span>
                </label>
                <Input
                  id="edit-phone-number-id"
                  type="text"
                  value={phoneNumberId}
                  onChange={(e) => setPhoneNumberId(e.target.value)}
                  placeholder="Ej: 105943288765432"
                  disabled={isSubmitting}
                  className="font-mono text-sm rounded-xl border-slate-200"
                  required
                />
                <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                  <HelpCircle className="w-3 h-3 text-slate-400 shrink-0" />
                  ID numérico del número telefónico en el Meta App Dashboard &gt; WhatsApp &gt; API Setup.
                </p>
              </div>

              {/* Access Token */}
              <div>
                <label
                  htmlFor="edit-access-token"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1"
                >
                  Access Token de Meta (Permanente)
                </label>
                <div className="relative">
                  <Input
                    id="edit-access-token"
                    type={showToken ? 'text' : 'password'}
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    placeholder="•••••••••••••••••••••••••••••••• (sin cambios)"
                    disabled={isSubmitting}
                    className="font-mono text-sm rounded-xl border-slate-200 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    title={showToken ? 'Ocultar token' : 'Mostrar token'}
                    tabIndex={-1}
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Déjalo en blanco para <strong className="text-slate-600">conservar el token actual</strong> guardado de forma segura y cifrada.
                </p>
              </div>

              {/* WABA ID (Optional) */}
              <div>
                <label
                  htmlFor="edit-waba-id"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1"
                >
                  WhatsApp Business Account ID (WABA ID) <span className="text-slate-400 text-[10px]">(Opcional)</span>
                </label>
                <Input
                  id="edit-waba-id"
                  type="text"
                  value={wabaId}
                  onChange={(e) => setWabaId(e.target.value)}
                  placeholder="Ej: 102938475610293"
                  disabled={isSubmitting}
                  className="font-mono text-sm rounded-xl border-slate-200"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Identificador de la cuenta de WhatsApp Business para sincronización de plantillas y catálogo.
                </p>
              </div>
            </>
          )}

          {/* Telegram Specific Fields */}
          {isTelegram && (
            <>
              <div>
                <label
                  htmlFor="edit-bot-token"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1"
                >
                  Bot Token de Telegram
                </label>
                <div className="flex gap-2">
                  <Input
                    id="edit-bot-token"
                    type="text"
                    value={botToken}
                    onChange={(e) => setBotToken(e.target.value)}
                    placeholder="•••••••••••••••• (dejar vacío para mantener el actual)"
                    disabled={isSubmitting}
                    className="font-mono text-sm rounded-xl border-slate-200 flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleValidateTelegram}
                    disabled={isSubmitting || validatingTelegram || !botToken.trim()}
                    className="rounded-xl text-xs shrink-0"
                  >
                    {validatingTelegram ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Validar'}
                  </Button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="edit-receive-mode"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1"
                >
                  Modo de recepción
                </label>
                <select
                  id="edit-receive-mode"
                  value={telegramReceiveMode}
                  onChange={(e) =>
                    setTelegramReceiveMode(e.target.value as 'webhook' | 'polling')
                  }
                  disabled={isSubmitting}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#075E54]/20"
                >
                  <option value="webhook">Webhook (Recomendado)</option>
                  <option value="polling">Long Polling</option>
                </select>
              </div>
            </>
          )}

          {/* Webhook Callback Reference */}
          {isCloud && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 text-xs text-slate-600 space-y-1">
              <span className="font-semibold text-slate-700 block">
                URL del Webhook en Meta:
              </span>
              <code className="block bg-white p-2 rounded-lg border border-slate-200 font-mono text-[11px] text-slate-800 break-all select-all">
                {typeof window !== 'undefined'
                  ? `${window.location.origin}/api/webhooks/meta`
                  : '/api/webhooks/meta'}
              </code>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-[#075E54] hover:bg-[#064E46] text-white px-5 text-xs font-semibold shadow-xs flex items-center gap-1.5"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verificando y guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Guardar cambios
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
